import { supabase } from '../../client';
import { BookingType } from '../../types';
import { format, parseISO } from 'date-fns';

// Admin functions
export const fetchAllBookings = async (): Promise<BookingType[]> => {
  try {
    const { data: bookingsData, error: bookingsError } = await supabase
      .from('bookings')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (bookingsError) {
      console.error('Error fetching bookings:', bookingsError);
      throw new Error(`Failed to fetch bookings: ${bookingsError.message}`);
    }
    
    if (!bookingsData || bookingsData.length === 0) {
      return [];
    }
    
    const bookingsWithClasses = await Promise.all(
      bookingsData.map(async (booking) => {
        const { data: classData } = await supabase
          .from('classes')
          .select('title, day, time, date')
          .eq('id', booking.class_id)
          .single();
        
        return {
          id: booking.id,
          userId: booking.user_id,
          classId: booking.class_id,
          firstName: booking.first_name,
          lastName: booking.last_name,
          email: booking.email,
          phone: booking.phone,
          createdAt: booking.created_at,
          className: classData?.title || 'Pottery Class',
          classDay: classData?.day || 'Unknown',
          classTime: classData?.time || 'Unknown',
          classDate: classData?.date || '',
          groupSize: booking.group_size || 1,
          groupNames: booking.group_names || [],
          bookingType: booking.booking_type,
          subscriptionId: booking.subscription_id,
          paymentStatus: booking.payment_status || 'pending'
        };
      })
    );
    
    return bookingsWithClasses;
  } catch (error: any) {
    console.error('Error processing bookings:', error);
    throw new Error(`Failed to process bookings: ${error.message}`);
  }
};

export const fetchUserBookings = async (userId: string, email: string): Promise<BookingType[]> => {
  try {
    const query = supabase
      .from('bookings')
      .select('*');
      
    if (userId) {
      query.eq('user_id', userId);
    } else if (email) {
      query.eq('email', email);
    } else {
      throw new Error('Either userId or email must be provided');
    }
    
    const { data: bookingsData, error: bookingsError } = await query.order('created_at', { ascending: false });
    
    if (bookingsError) {
      console.error('Error fetching user bookings:', bookingsError);
      throw new Error(`Failed to fetch user bookings: ${bookingsError.message}`);
    }
    
    if (!bookingsData || bookingsData.length === 0) {
      return [];
    }
    
    const bookingsWithClasses = await Promise.all(
      bookingsData.map(async (booking) => {
        const { data: classData } = await supabase
          .from('classes')
          .select('title, day, time, date')
          .eq('id', booking.class_id)
          .single();
        
        return {
          id: booking.id,
          userId: booking.user_id,
          classId: booking.class_id,
          firstName: booking.first_name,
          lastName: booking.last_name,
          email: booking.email,
          phone: booking.phone,
          createdAt: booking.created_at,
          className: classData?.title || 'Pottery Class',
          classDay: classData?.day || 'Unknown',
          classTime: classData?.time || 'Unknown',
          classDate: classData?.date || '',
          groupSize: booking.group_size || 1,
          groupNames: booking.group_names || [],
          bookingType: booking.booking_type,
          subscriptionId: booking.subscription_id,
          paymentStatus: booking.payment_status || 'pending'
        };
      })
    );
    
    return bookingsWithClasses;
  } catch (error: any) {
    console.error('Error processing user bookings:', error);
    throw new Error(`Failed to process user bookings: ${error.message}`);
  }
};

export const fetchSubscriptionBookings = async (subscriptionId: string): Promise<BookingType[]> => {
  try {
    const { data: bookingsData, error: bookingsError } = await supabase
      .from('bookings')
      .select('*')
      .eq('subscription_id', subscriptionId)
      .order('created_at', { ascending: false });
    
    if (bookingsError) {
      console.error('Error fetching subscription bookings:', bookingsError);
      throw new Error(`Failed to fetch subscription bookings: ${bookingsError.message}`);
    }
    
    if (!bookingsData || bookingsData.length === 0) {
      return [];
    }
    
    const bookingsWithClasses = await Promise.all(
      bookingsData.map(async (booking) => {
        const { data: classData } = await supabase
          .from('classes')
          .select('title, day, time, date')
          .eq('id', booking.class_id)
          .single();
        
        return {
          id: booking.id,
          userId: booking.user_id,
          classId: booking.class_id,
          firstName: booking.first_name,
          lastName: booking.last_name,
          email: booking.email,
          phone: booking.phone,
          createdAt: booking.created_at,
          className: classData?.title || 'Pottery Class',
          classDay: classData?.day || 'Unknown',
          classTime: classData?.time || 'Unknown',
          classDate: classData?.date || '',
          bookingType: booking.booking_type,
          subscriptionId: booking.subscription_id,
          paymentStatus: booking.payment_status || 'pending'
        };
      })
    );
    
    return bookingsWithClasses;
  } catch (error: any) {
    console.error('Error processing subscription bookings:', error);
    throw new Error(`Failed to process subscription bookings: ${error.message}`);
  }
};

export const fetchBookingById = async (bookingId: string): Promise<BookingType> => {
  try {
    console.log(`Fetching booking with ID: ${bookingId}`);
    
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .maybeSingle();
    
    if (bookingError) {
      console.error('Error fetching booking:', bookingError);
      throw new Error(`Booking not found: ${bookingError.message}`);
    }
    
    if (!booking) {
      console.error('No booking found with ID:', bookingId);
      throw new Error('Booking not found');
    }
    
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('title, day, time, date')
      .eq('id', booking.class_id)
      .maybeSingle();
    
    if (classError) {
      console.error('Error fetching class details:', classError);
    }
    
    return {
      id: booking.id,
      userId: booking.user_id,
      classId: booking.class_id,
      firstName: booking.first_name,
      lastName: booking.last_name,
      email: booking.email,
      phone: booking.phone,
      createdAt: booking.created_at,
      className: classData?.title || 'Pottery Class',
      classDay: classData?.day || 'Unknown',
      classTime: classData?.time || 'Unknown',
      classDate: classData?.date || '',
      groupSize: booking.group_size || 1,
      groupNames: booking.group_names || [],
      bookingType: booking.booking_type,
      subscriptionId: booking.subscription_id,
      paymentStatus: booking.payment_status || 'pending'
    };
  } catch (error: any) {
    console.error('Error fetching booking by ID:', error);
    throw new Error(error.message || 'Failed to fetch booking');
  }
};
